import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../Layout';
import aoAPI from '../../api/aoAPI';
import ClientSearchSelect from './ClientSearchSelect';
import {
  ArrowDownTrayIcon,
  PrinterIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

// ── Constants ──────────────────────────────────────────────────────────────────

const TAX_TYPES_MONTHLY = ['PT', 'VAT', 'WE', 'Excise', 'DST', 'ITR'];
const PROCESS_TYPES     = ['SEC', 'BIR', 'BusinessPermit', 'TaxClearance', 'PhilGeps', 'Notarial', 'DocStamp', 'OtherExpenses'];

const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
const MONTHLY_COLS = [
  { key: 'jan', label: 'Jan' }, { key: 'feb', label: 'Feb' }, { key: 'mar', label: 'Mar' },
  { key: 'q1',  label: 'Q1',  isQ: true },
  { key: 'apr', label: 'Apr' }, { key: 'may', label: 'May' }, { key: 'jun', label: 'Jun' },
  { key: 'q2',  label: 'Q2',  isQ: true },
  { key: 'jul', label: 'Jul' }, { key: 'aug', label: 'Aug' }, { key: 'sep', label: 'Sep' },
  { key: 'q3',  label: 'Q3',  isQ: true },
  { key: 'oct', label: 'Oct' }, { key: 'nov', label: 'Nov' }, { key: 'dec', label: 'Dec' },
  { key: 'q4',  label: 'Q4',  isQ: true },
  { key: 'annual', label: 'Annual', isAnnual: true },
];

const PROCESS_LABELS = {
  SEC: 'SEC', BIR: 'BIR', BusinessPermit: 'Bus. Permit',
  TaxClearance: 'Tax Clear.', PhilGeps: 'PhilGeps',
  Notarial: 'Notarial', DocStamp: 'Doc Stamp', OtherExpenses: 'Other',
};

const fmt = (n) => (n && n !== 0) ? new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : '';

// Cell colour based on paid status
// We don't have per-cell isPaid in the pivot — only totals.
// Colour is applied when the cell maps to a known set of transactions.
// For simplicity, colour-code by amount presence (green = has value, white = 0).
// A more granular approach would require per-cell isPaid from the backend.
const cellBg = (val) => {
  if (!val || val === 0) return '';
  return 'bg-green-50';
};

// ── CSV Export ─────────────────────────────────────────────────────────────────

function buildCSV(clients, fiscalYear) {
  const rows = [];

  // Header row 1: group headers
  const h1 = ['CLIENT NAME', 'IN-CHARGE'];
  for (const tt of TAX_TYPES_MONTHLY) {
    for (let i = 0; i < MONTHLY_COLS.length; i++) h1.push(i === 0 ? tt : '');
  }
  h1.push('TOTAL TAX');
  for (let i = 0; i < MONTHLY_COLS.length; i++) h1.push(i === 0 ? 'PF' : '');
  for (const pt of PROCESS_TYPES) h1.push(PROCESS_LABELS[pt]);
  h1.push('TOTAL PROC.', 'GRAND TOTAL');
  rows.push(h1);

  // Header row 2: month/quarter labels
  const h2 = ['', ''];
  for (const tt of TAX_TYPES_MONTHLY) {
    for (const col of MONTHLY_COLS) h2.push(col.label);
  }
  h2.push('');
  for (const col of MONTHLY_COLS) h2.push(col.label);
  for (const pt of PROCESS_TYPES) h2.push('');
  h2.push('', '');
  rows.push(h2);

  // Data rows
  for (const c of clients) {
    const row = [c.name, c.inCharge?.name || ''];
    for (const tt of TAX_TYPES_MONTHLY) {
      for (const col of MONTHLY_COLS) {
        row.push(c.taxes?.[tt]?.[col.key] || 0);
      }
    }
    row.push(c.totals?.taxTotal || 0);
    for (const col of MONTHLY_COLS) {
      row.push(c.pf?.[col.key] || 0);
    }
    for (const pt of PROCESS_TYPES) {
      row.push(c.processing?.[pt] || 0);
    }
    row.push(c.processing?.total || 0, c.totals?.grandTotal || 0);
    rows.push(row);
  }

  return rows.map(r =>
    r.map(v => (typeof v === 'string' ? `"${v.replace(/"/g, '""')}"` : v)).join(',')
  ).join('\n');
}

// ── Component ─────────────────────────────────────────────────────────────────

const AOWorkingPaperReport = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [inCharge,   setInCharge]   = useState('');
  const [report,     setReport]     = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const tableRef = useRef(null);

  useEffect(() => {
    aoAPI.getClients().then(r => setClients(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    aoAPI.getStaff().then(r => setStaffList(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { fiscalYear };
      if (inCharge) params.inCharge = inCharge;
      const res = await aoAPI.getWorkingPaper(params);
      setReport(res.data);
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [fiscalYear, inCharge]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    if (!visibleClients.length) return;
    const csv  = buildCSV(visibleClients, report.fiscalYear);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `working-paper-${report.fiscalYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => window.print();

  const reportClients = report?.clients || [];
  const visibleClients = selectedClient
    ? reportClients.filter(c => String(c._id) === String(selectedClient))
    : reportClients;

  // Build totals row (sum of each column across all clients)
  const colTotals = {
    taxes: Object.fromEntries(TAX_TYPES_MONTHLY.map(tt => [tt, Object.fromEntries(MONTHLY_COLS.map(c => [c.key, 0]))])),
    pf:    Object.fromEntries(MONTHLY_COLS.map(c => [c.key, 0])),
    processing: Object.fromEntries(PROCESS_TYPES.map(pt => [pt, 0])),
    processingTotal: 0,
    taxTotal: 0,
    grandTotal: 0,
  };
  for (const c of visibleClients) {
    for (const tt of TAX_TYPES_MONTHLY) {
      for (const col of MONTHLY_COLS) {
        colTotals.taxes[tt][col.key] += c.taxes?.[tt]?.[col.key] || 0;
      }
    }
    for (const col of MONTHLY_COLS) colTotals.pf[col.key] += c.pf?.[col.key] || 0;
    for (const pt of PROCESS_TYPES) colTotals.processing[pt] += c.processing?.[pt] || 0;
    colTotals.processingTotal += c.processing?.total || 0;
    colTotals.taxTotal        += c.totals?.taxTotal  || 0;
    colTotals.grandTotal      += c.totals?.grandTotal || 0;
  }

  return (
    <Layout pageTitle="Working Paper Report">
      <div className="p-4 print:p-0">
        {/* Controls */}
        <div className="flex flex-wrap items-end gap-3 mb-4 print:hidden">
          <div className="min-w-[280px] flex-1 max-w-md">
            <label className="block text-xs font-medium text-gray-600 mb-1">Client</label>
            <ClientSearchSelect
              clients={clients}
              value={selectedClient}
              onChange={(id) => { setSelectedClient(id); }}
              placeholder="— Search client —"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fiscal Year</label>
            <input
              type="number"
              value={fiscalYear}
              onChange={e => setFiscalYear(parseInt(e.target.value, 10))}
              min={2000} max={2100}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-28 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <FunnelIcon className="inline h-3 w-3 mr-1" />
              In-Charge Filter
            </label>
            <select
              value={inCharge}
              onChange={e => setInCharge(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Staff</option>
              {staffList.map(s => (
                <option key={s._id} value={s._id}>{s.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleExport}
            disabled={!visibleClients.length}
            className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export CSV
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm px-4 py-2 rounded-lg transition-colors"
          >
            <PrinterIcon className="h-4 w-4" />
            Print
          </button>
        </div>

        {/* Summary strip */}
        {report?.summary && (
          <div className="flex flex-wrap gap-4 mb-4 print:hidden">
            {[
              { label: 'Total Clients',    value: report.summary.totalClients },
              { label: 'Total Collected',  value: `₱${fmt(report.summary.totalCollected) || '0.00'}` },
              { label: 'Total Pending',    value: `₱${fmt(report.summary.totalPending) || '0.00'}` },
            ].map(s => (
              <div key={s.label} className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-center">
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className="font-semibold text-gray-800 text-sm mt-0.5">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

        {loading ? (
          <p className="text-sm text-gray-400">Loading report…</p>
        ) : visibleClients.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-12">No data for {fiscalYear}.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200" ref={tableRef}>
            <table className="text-xs border-collapse min-w-max">
              {/* ── Column Group Headers ── */}
              <thead>
                {/* Row 1: group labels */}
                <tr className="bg-gray-800 text-white">
                  <th rowSpan={2} className="sticky left-0 z-20 bg-gray-800 border border-gray-700 px-3 py-2 text-left whitespace-nowrap min-w-[160px]">
                    CLIENT NAME
                  </th>
                  <th rowSpan={2} className="sticky left-[160px] z-20 bg-gray-800 border border-gray-700 px-3 py-2 text-left whitespace-nowrap min-w-[100px]">
                    IN-CHARGE
                  </th>
                  {TAX_TYPES_MONTHLY.map(tt => (
                    <th key={tt} colSpan={MONTHLY_COLS.length} className="border border-gray-700 px-2 py-1 text-center bg-teal-700">
                      {tt}
                    </th>
                  ))}
                  <th rowSpan={2} className="border border-gray-700 px-2 py-1 text-center bg-gray-700 whitespace-nowrap">
                    TOTAL TAX
                  </th>
                  <th colSpan={MONTHLY_COLS.length} className="border border-gray-700 px-2 py-1 text-center bg-blue-700">
                    PF
                  </th>
                  {PROCESS_TYPES.map(pt => (
                    <th key={pt} rowSpan={2} className="border border-gray-700 px-2 py-1 text-center bg-purple-700 whitespace-nowrap">
                      {PROCESS_LABELS[pt]}
                    </th>
                  ))}
                  <th rowSpan={2} className="border border-gray-700 px-2 py-1 text-center bg-purple-800 whitespace-nowrap">
                    TOTAL PROC.
                  </th>
                  <th rowSpan={2} className="border border-gray-700 px-2 py-1 text-center bg-gray-900 whitespace-nowrap">
                    GRAND TOTAL
                  </th>
                </tr>
                {/* Row 2: month/quarter labels */}
                <tr className="bg-gray-700 text-gray-200">
                  {TAX_TYPES_MONTHLY.map(tt =>
                    MONTHLY_COLS.map(col => (
                      <th
                        key={`${tt}-${col.key}`}
                        className={`border border-gray-600 px-1.5 py-1 text-center whitespace-nowrap ${
                          col.isAnnual ? 'bg-teal-900 font-bold' : col.isQ ? 'bg-teal-800' : 'bg-teal-700'
                        }`}
                      >
                        {col.label}
                      </th>
                    ))
                  )}
                  {MONTHLY_COLS.map(col => (
                    <th
                      key={`pf-${col.key}`}
                      className={`border border-gray-600 px-1.5 py-1 text-center whitespace-nowrap ${
                        col.isAnnual ? 'bg-blue-900 font-bold' : col.isQ ? 'bg-blue-800' : 'bg-blue-700'
                      }`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* ── Data Rows ── */}
              <tbody>
                {visibleClients.map((c, idx) => (
                  <tr key={c._id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {/* Frozen cols */}
                    <td className={`sticky left-0 z-10 border border-gray-200 px-3 py-1.5 font-medium text-gray-800 whitespace-nowrap ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      {c.name}{c.code ? <span className="text-gray-400 font-mono ml-1 text-[10px]">{c.code}</span> : ''}
                    </td>
                    <td className={`sticky left-[160px] z-10 border border-gray-200 px-3 py-1.5 text-gray-600 whitespace-nowrap ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      {c.inCharge?.name || '—'}
                    </td>

                    {/* Tax type cells */}
                    {TAX_TYPES_MONTHLY.map(tt =>
                      MONTHLY_COLS.map(col => {
                        const val = c.taxes?.[tt]?.[col.key] || 0;
                        return (
                          <td
                            key={`${tt}-${col.key}`}
                            className={`border border-gray-100 px-1.5 py-1 text-right font-mono whitespace-nowrap ${
                              val ? (col.isAnnual ? 'bg-teal-100 font-semibold text-teal-800' : col.isQ ? 'bg-teal-50 text-teal-700' : 'bg-green-50 text-green-800') : 'text-gray-300'
                            }`}
                          >
                            {fmt(val)}
                          </td>
                        );
                      })
                    )}

                    {/* Total Tax */}
                    <td className="border border-gray-200 px-2 py-1 text-right font-mono font-semibold text-gray-800 bg-gray-100 whitespace-nowrap">
                      {fmt(c.totals?.taxTotal)}
                    </td>

                    {/* PF cells */}
                    {MONTHLY_COLS.map(col => {
                      const val = c.pf?.[col.key] || 0;
                      return (
                        <td
                          key={`pf-${col.key}`}
                          className={`border border-gray-100 px-1.5 py-1 text-right font-mono whitespace-nowrap ${
                            val ? (col.isAnnual ? 'bg-blue-100 font-semibold text-blue-800' : col.isQ ? 'bg-blue-50 text-blue-700' : 'bg-blue-50 text-blue-800') : 'text-gray-300'
                          }`}
                        >
                          {fmt(val)}
                        </td>
                      );
                    })}

                    {/* Processing cells */}
                    {PROCESS_TYPES.map(pt => {
                      const val = c.processing?.[pt] || 0;
                      return (
                        <td key={pt} className={`border border-gray-100 px-1.5 py-1 text-right font-mono whitespace-nowrap ${val ? 'bg-purple-50 text-purple-800' : 'text-gray-300'}`}>
                          {fmt(val)}
                        </td>
                      );
                    })}

                    {/* Processing total */}
                    <td className="border border-gray-200 px-2 py-1 text-right font-mono font-semibold text-purple-800 bg-purple-50 whitespace-nowrap">
                      {fmt(c.processing?.total)}
                    </td>

                    {/* Grand total */}
                    <td className="border border-gray-200 px-2 py-1 text-right font-mono font-bold text-gray-900 bg-gray-200 whitespace-nowrap">
                      {fmt(c.totals?.grandTotal)}
                    </td>
                  </tr>
                ))}

                {/* ── Totals row ── */}
                <tr className="bg-gray-100 font-semibold text-gray-800 border-t-2 border-gray-400">
                  <td className="sticky left-0 z-10 bg-gray-200 border border-gray-300 px-3 py-2 font-bold whitespace-nowrap">TOTAL</td>
                  <td className="sticky left-[160px] z-10 bg-gray-200 border border-gray-300 px-3 py-2"></td>
                  {TAX_TYPES_MONTHLY.map(tt =>
                    MONTHLY_COLS.map(col => {
                      const val = colTotals.taxes[tt][col.key];
                      return (
                        <td key={`t-${tt}-${col.key}`} className={`border border-gray-300 px-1.5 py-2 text-right font-mono whitespace-nowrap ${col.isAnnual || col.isQ ? 'bg-gray-300 font-bold' : ''}`}>
                          {fmt(val)}
                        </td>
                      );
                    })
                  )}
                  <td className="border border-gray-300 px-2 py-2 text-right font-mono font-bold bg-gray-300 whitespace-nowrap">
                    {fmt(colTotals.taxTotal)}
                  </td>
                  {MONTHLY_COLS.map(col => {
                    const val = colTotals.pf[col.key];
                    return (
                      <td key={`t-pf-${col.key}`} className={`border border-gray-300 px-1.5 py-2 text-right font-mono whitespace-nowrap ${col.isAnnual || col.isQ ? 'bg-gray-300 font-bold' : ''}`}>
                        {fmt(val)}
                      </td>
                    );
                  })}
                  {PROCESS_TYPES.map(pt => (
                    <td key={`t-${pt}`} className="border border-gray-300 px-1.5 py-2 text-right font-mono whitespace-nowrap">
                      {fmt(colTotals.processing[pt])}
                    </td>
                  ))}
                  <td className="border border-gray-300 px-2 py-2 text-right font-mono font-bold whitespace-nowrap">{fmt(colTotals.processingTotal)}</td>
                  <td className="border border-gray-300 px-2 py-2 text-right font-mono font-bold bg-gray-300 whitespace-nowrap">{fmt(colTotals.grandTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Colour legend */}
        {!loading && clients.length > 0 && (
          <div className="mt-3 flex gap-4 text-xs text-gray-500 print:hidden">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-green-100 border border-green-300"></span> Has amount</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-teal-50 border border-teal-200"></span> Quarterly total</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-teal-100 border border-teal-300"></span> Annual total</span>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AOWorkingPaperReport;
